<template>
  <v-card class="mt-5 pa-4" :loading="loading">
    <div v-if="loading === false && report.number">
      <p>Название проекта: <v-chip label small>{{report.project}}</v-chip></p>
      <p>Номер договора: <v-chip label small>{{report.number}}</v-chip></p>
      <p>Итоговая сумма по договору: <v-chip label small>{{report.total_amount_contract}}</v-chip></p>
      <p v-if="report.forecast_total_amount_contract">
        Прогноз суммы по договору: <v-chip label small>{{report.forecast_total_amount_contract}}</v-chip>
      </p>
    </div>

    <v-expansion-panels
      v-if="loading === false && report.number && 'appendices' in report && report.appendices.length"
      focusable
    >
      <v-expansion-panel
        v-for="(appendix, index) in report.appendices"
        :key="index"
      >
        <v-expansion-panel-header>
          {{ appendix.number }}
          <div>
          <v-badge
            class="ml-3"
            bordered
            color="success"
            overlap
          >
            <v-chip
              color="info"
              outlined
              small
            >Кол-во дней</v-chip>
            <template #badge>
              {{ appendix.work_days }}
            </template>
          </v-badge>

          <v-badge
            class="ml-12"
            bordered
            color="success"
            overlap
          >
            <v-chip
              color="info"
              outlined
              small
            >Итоговая сумма</v-chip>
            <template #badge>
              {{ appendix.total_amount_appendix }}
            </template>
          </v-badge>

          <v-badge
            v-if="appendix.forecast_total_amount_appendix"
            class="ml-12"
            bordered
            color="warning"
            overlap
          >
            <v-chip
              color="info"
              outlined
              small
            >Прогноз суммы</v-chip>
            <template #badge>
              {{ appendix.forecast_total_amount_appendix }}
            </template>
          </v-badge>
          </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <div v-if="'additionalServices' in appendix && appendix.additionalServices.length">
            <p class="text-h6 ma-0 mt-3">Дополнительные услуги</p>
            <v-simple-table style="width: fit-content;">
              <template v-slot:default>
                <tbody>
                  <tr
                    v-for="(item, index) in appendix.additionalServices"
                    :key="'' + item.value + index"
                  >
                    <td>{{ item.title }}</td>
                    <td>{{ item.value }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <div v-if="'operatorPackages' in appendix && appendix.operatorPackages.length">
            <v-divider class="my-5" />
            <p class="text-h6 ma-0 mt-3">Пакеты операторов</p>

            <div
              v-for="(operatorPackage, index) in appendix.operatorPackages"
              :key="'operatorPackages' + index"
            >
              <div
                v-for="section in packageSections(operatorPackage)"
                :key="section.key"
              >
                <p v-if="section.title" class="text-subtitle-1 font-weight-bold ma-0 mt-3 mb-2">{{ section.title }}</p>
                <div class="d-flex flex-wrap align-start package-row">
                <v-simple-table style="width: fit-content;">
                  <tbody>
                    <tr
                      v-for="(item, rowIndex) in section.data.rows"
                      :key="'operatorPackageRow' + section.key + item.value + rowIndex"
                    >
                      <td>{{ item.title }}</td>
                      <td>{{ item.value }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>

                <v-card outlined
                  v-if="section.data.general_statistics"
                  class="pa-4"
                >
                  <p class="package-control__title">Контроль исполнения пакета</p>

                  <div class="package-control__body">
                    <div class="package-control__chart">
                      <v-progress-circular
                        :value="usedPercentRing(section.data.general_statistics)"
                        :size="140"
                        :width="14"
                        color="success"
                        rotate="-90"
                      >
                        <div class="package-control__chart-center">
                          <div class="package-control__percent">
                            {{ usedPercentLabel(section.data.general_statistics) }}%
                          </div>
                          <div class="package-control__percent-label">использовано</div>
                        </div>
                      </v-progress-circular>
                    </div>

                    <div class="package-control__stats">
                      <div class="package-control__stat">
                        <span class="package-control__stat-label">Пакет, мин</span>
                        <span class="package-control__stat-value">
                          {{ formatNumber(section.data.general_statistics.limit) }}
                        </span>
                      </div>
                      <div class="package-control__stat">
                        <span class="package-control__stat-label">Факт, мин</span>
                        <span class="package-control__stat-value">
                          {{ formatNumber(section.data.general_statistics.total_duration) }}
                        </span>
                      </div>
                      <div class="package-control__stat">
                        <span class="package-control__stat-label">
                          {{ isOverPackage(section.data.general_statistics) ? 'Превышение, мин' : 'Осталось, мин' }}
                        </span>
                        <span class="package-control__stat-value">
                          {{ formatNumber(Math.abs(section.data.general_statistics.remains)) }}
                        </span>
                      </div>
                      <div class="package-control__stat">
                        <span class="package-control__stat-label">Средняя длина диалога</span>
                        <span class="package-control__stat-value">
                          {{ formatAverage(section.data.general_statistics.average_duration) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </v-card>
                </div>
              </div>

              <v-divider
                v-if="index !== appendix.operatorPackages.length - 1"
                class="my-5"
              />
            </div>
          </div>


        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

  </v-card>
</template>

<script>
export default {
  name: 'ContractReport',
  props: {
    report: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    packageSections(operatorPackage) {
      if (!operatorPackage.forecast) return [{ key: 'fact', title: null, data: operatorPackage }]
      return [
        { key: 'fact', title: 'Факт', data: operatorPackage },
        { key: 'forecast', title: 'Прогноз на конец месяца', data: operatorPackage.forecast },
      ]
    },
    isOverPackage(stats) {
      return Number(stats.total_duration) > Number(stats.limit)
    },
    usedPercentRaw(stats) {
      const limit = Number(stats.limit)
      if (!limit) return 0
      return (Number(stats.total_duration) / limit) * 100
    },
    usedPercentRing(stats) {
      return Math.min(100, Math.max(0, this.usedPercentRaw(stats)))
    },
    usedPercentLabel(stats) {
      return Math.round(this.usedPercentRaw(stats))
    },
    formatNumber(value) {
      if (value == null || value === '') return '—'
      return Number(value).toLocaleString('ru-RU')
    },
    formatAverage(value) {
      if (value == null || value === '') return '—'
      return `${Number(value).toLocaleString('ru-RU')} мин`
    },
  },
}
</script>

<style scoped lang="scss">
.package-row {
  gap: 30px;
}

.package-control {
  min-width: 360px;
  max-width: 480px;
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px 24px;
  background: #fff;
}

.package-control__title {
  font-weight: 700;
  font-size: 1.05rem;
  margin: 0 0 16px;
}

.package-control__body {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.package-control__chart-center {
  text-align: center;
  line-height: 1.15;
}

.package-control__percent {
  font-size: 1.5rem;
  font-weight: 700;
}

.package-control__percent-label {
  font-size: 0.75rem;
  color: #9e9e9e;
}

.package-control__stats {
  flex: 1;
  min-width: 180px;
}

.package-control__stat {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid #eeeeee;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
}

.package-control__stat-label {
  color: #9e9e9e;
  font-size: 0.9rem;
}

.package-control__stat-value {
  font-weight: 700;
  white-space: nowrap;
}
</style>
